import { useState, useEffect, useCallback, useRef } from 'react';
import useSwipeTabs from '../hooks/useSwipeTabs';
import {
    FolderOpen, Shield, Upload, Search, Grid3X3, List, Filter,
    ArrowUpDown, Eye, Download, Trash2, Plus, X, Loader2,
    FileText, Image, Film, Music, Paperclip, FolderPlus,
    ChevronRight, ChevronDown, Tag, ExternalLink, Copy, Check, Pencil,
    CheckSquare, Square, FolderInput, Move,
    FileSpreadsheet, FileType, Presentation, File,
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import TabBar from '../components/layout/TabBar';

// ── Design tokeny FofrLama OS ─────────────────────────────
/**
 * Barvy Správce souborů.
 *
 * Neutrální tóny byly natvrdo tmavé (#111, #e2e2e2), takže modul zůstal tmavý
 * i ve světlém motivu — včetně bílého popisku aktivní záložky na bílé. Teď
 * ukazují na tokeny z `app.css`, takže se přepnou s motivem samy; akcent,
 * stavové barvy a zaoblení zůstávají hodnotami, protože se skládají do hexu
 * s průhledností (`${DS.accent}30`).
 */
const DS = {
    bg:          'var(--surface-base)',
    card:        'var(--surface-raised)',
    cardBorder:  'var(--border-default)',
    cardHover:   'var(--surface-elevated)',
    inputBg:     'var(--surface-elevated)',
    inputBorder: 'var(--border-strong)',
    accent:      '#0AA5BE',
    accentLight: '#0CC0DF',
    accentBg:    '#0CC0DF30',
    accentBg15:  '#0CC0DF15',
    accentBg25:  '#0CC0DF25',
    text:        'var(--text-primary)',
    textSec:     'var(--text-secondary)',
    textTer:     'var(--text-muted)',
    textMuted:   'var(--text-muted)',
    textHover:   'var(--text-primary)',
    textLight:   'var(--text-primary)',
    divider:     'var(--border-subtle)',
    tabBorder:   'var(--border-default)',
    danger:      '#e07a5f',
    dangerBg:    '#D85A3020',
    success:     '#1D9E75',
    successBg:   '#1D9E7520',
    badge:       '#FF8A00',
    radius:      7,
    radiusLg:    10,
    radiusCard:  9,
};

const SECTION_COLOR = DS.accentLight;
const SECTION_BG = DS.accentBg;

const ALL_TABS = [
    { id: 'spravce', label: 'Správce souborů', Icon: FolderOpen, admin: false },
    { id: 'pristupy', label: 'Přístupy ke složkám', Icon: Shield, admin: true },
];

const TYP_CFG = {
    foto:     { label: 'Foto',     color: DS.accentLight, bg: DS.accentBg25, Icon: Image },
    video:    { label: 'Video',    color: '#0CC0DF',      bg: 'rgba(12,192,223,0.15)', Icon: Film },
    dokument: { label: 'Dokument', color: DS.danger,      bg: DS.dangerBg,  Icon: FileText },
    audio:    { label: 'Audio',    color: DS.accentLight,  bg: DS.accentBg25, Icon: Music },
    jiny:     { label: 'Jiný',     color: DS.textSec,     bg: 'rgba(136,136,136,0.15)', Icon: Paperclip },
};

// Specifické ikony a barvy dle přípony/mime
const FILE_EXT_MAP = {
    pdf:  { label: 'PDF',  color: '#e07a5f', bg: '#D85A3020', Icon: FileText },
    doc:  { label: 'Word', color: '#4285F4', bg: 'rgba(66,133,244,0.15)', Icon: FileType },
    docx: { label: 'Word', color: '#4285F4', bg: 'rgba(66,133,244,0.15)', Icon: FileType },
    xls:  { label: 'Excel', color: '#1D9E75', bg: '#1D9E7520', Icon: FileSpreadsheet },
    xlsx: { label: 'Excel', color: '#1D9E75', bg: '#1D9E7520', Icon: FileSpreadsheet },
    csv:  { label: 'CSV',   color: '#1D9E75', bg: '#1D9E7520', Icon: FileSpreadsheet },
    ppt:  { label: 'PPT',   color: '#FF8A00', bg: 'rgba(255,138,0,0.15)', Icon: Presentation },
    pptx: { label: 'PPT',   color: '#FF8A00', bg: 'rgba(255,138,0,0.15)', Icon: Presentation },
    txt:  { label: 'TXT',   color: DS.textSec, bg: 'rgba(136,136,136,0.15)', Icon: FileText },
    zip:  { label: 'ZIP',   color: '#f59e0b', bg: 'rgba(245,158,11,0.15)', Icon: File },
    rar:  { label: 'RAR',   color: '#f59e0b', bg: 'rgba(245,158,11,0.15)', Icon: File },
    svg:  { label: 'SVG',   color: '#FF8A00', bg: 'rgba(255,138,0,0.15)', Icon: Image },
    gif:  { label: 'GIF',   color: DS.accentLight, bg: DS.accentBg25, Icon: Image },
    png:  { label: 'PNG',   color: DS.accentLight, bg: DS.accentBg25, Icon: Image },
    jpg:  { label: 'JPG',   color: DS.accentLight, bg: DS.accentBg25, Icon: Image },
    jpeg: { label: 'JPG',   color: DS.accentLight, bg: DS.accentBg25, Icon: Image },
    webp: { label: 'WebP',  color: DS.accentLight, bg: DS.accentBg25, Icon: Image },
    mp4:  { label: 'MP4',   color: '#0CC0DF', bg: 'rgba(12,192,223,0.15)', Icon: Film },
    mov:  { label: 'MOV',   color: '#0CC0DF', bg: 'rgba(12,192,223,0.15)', Icon: Film },
    mp3:  { label: 'MP3',   color: DS.accentLight, bg: DS.accentBg25, Icon: Music },
    wav:  { label: 'WAV',   color: DS.accentLight, bg: DS.accentBg25, Icon: Music },
};

function getFileCfg(soubor) {
    // Nejdříve dle přípony
    const ext = soubor.nazev?.split('.').pop()?.toLowerCase();
    if (ext && FILE_EXT_MAP[ext]) return FILE_EXT_MAP[ext];
    // Fallback dle mime
    if (soubor.mime_type?.includes('pdf')) return FILE_EXT_MAP.pdf;
    if (soubor.mime_type?.includes('word') || soubor.mime_type?.includes('document')) return FILE_EXT_MAP.docx;
    if (soubor.mime_type?.includes('sheet') || soubor.mime_type?.includes('excel')) return FILE_EXT_MAP.xlsx;
    if (soubor.mime_type?.includes('presentation') || soubor.mime_type?.includes('powerpoint')) return FILE_EXT_MAP.pptx;
    // Fallback dle typu
    return TYP_CFG[soubor.typ] || TYP_CFG.jiny;
}

const UROVEN_CFG = {
    cist:     { label: 'Číst',    color: '#93c5fd', bg: 'rgba(96,165,250,0.2)' },
    nahravat: { label: '+Nahrát', color: '#FF8A00', bg: 'rgba(255,138,0,0.2)' },
    plny:     { label: 'Plný',    color: '#975ACD', bg: 'rgba(108,65,146,0.3)' },
};

const SKUPINA_TYP_LABELS = {
    role: 'Role', pozice: 'Pozice', oddeleni: 'Oddělení', kariera: 'Kariéra', uzivatel: 'Uživatel',
};

function formatSize(bytes) {
    if (!bytes) return '—';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
}

function formatDate(d) {
    if (!d) return '—';
    const dt = new Date(d);
    return dt.toLocaleDateString('cs-CZ') + ' ' + dt.toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' });
}

const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content;

// ═══════════════════════════════════════════════════════════
// ── FOLDER TREE ───────────────────────────────────────────

// ── Nabídka ikon pro složky ────────────────────────────────
const FOLDER_ICONS = [
    '📁', '📂', '📸', '🎬', '📄', '🎵', '📋', '🛒', '💰', '📱',
    '🎉', '👤', '🔒', '⭐', '🏷️', '📊', '🍔', '🥗', '🍕', '☕',
    '📦', '🎨', '🔧', '📝', '💼', '🏠', '🌍', '🎓', '❤️', '🔥',
];

// ── Inline formulář pro složku ─────────────────────────────
function SlozkaForm({ editId, initialForm, isDark, onSave, onCancel }) {
    const [form, setForm] = useState(initialForm);
    const [saving, setSaving] = useState(false);
    const [showIcons, setShowIcons] = useState(false);

    const handleSave = async () => {
        if (!form.nazev) return;
        setSaving(true);
        try {
            const url = editId ? `/api/media/slozky/${editId}` : '/api/media/slozky';
            const method = editId ? 'PUT' : 'POST';
            const body = editId
                ? { nazev: form.nazev, ikona: form.ikona, popis: form.popis }
                : { nazev: form.nazev, ikona: form.ikona, popis: form.popis, rodic_id: form.rodic_id || null };
            const r = await fetch(url, {
                method, headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrf() },
                body: JSON.stringify(body),
            });
            if (r.ok) { onSave(); }
            else { const err = await r.json().catch(() => ({})); alert(err.error || 'Chyba'); }
        } catch (e) { console.error(e); }
        setSaving(false);
    };

    const inputStyle = { background: DS.inputBg, color: DS.text, border: `1px solid ${DS.inputBorder}`, borderRadius: DS.radius, colorScheme: 'dark', boxSizing: 'border-box' };

    return (
        <div style={{ background: DS.card, border: `1px solid ${DS.cardBorder}`, borderRadius: DS.radius, padding: 8, marginBottom: 8, overflow: 'hidden' }}>
            {/* Název + ikona */}
            <div className="flex gap-1 mb-1" style={{ minWidth: 0 }}>
                <div style={{ position: 'relative', flexShrink: 0 }}>
                    <button
                        onClick={() => setShowIcons(!showIcons)}
                        style={{ ...inputStyle, width: 30, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, cursor: 'pointer', padding: 0 }}
                        title="Vybrat ikonu"
                    >
                        {form.ikona || '📁'}
                    </button>
                    {showIcons && (
                        <div style={{
                            position: 'absolute', top: 30, left: 0, zIndex: 50,
                            background: DS.card, border: `1px solid ${DS.cardBorder}`, borderRadius: DS.radius,
                            padding: 6, width: 180,
                            display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 2,
                            boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                        }}>
                            {FOLDER_ICONS.map(icon => (
                                <button
                                    key={icon}
                                    onClick={() => { setForm({ ...form, ikona: icon }); setShowIcons(false); }}
                                    style={{
                                        width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: 14, borderRadius: 4, border: 'none', cursor: 'pointer',
                                        background: form.ikona === icon ? DS.accentBg : 'transparent',
                                    }}
                                >
                                    {icon}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                <input
                    value={form.nazev}
                    onChange={e => setForm({ ...form, nazev: e.target.value })}
                    placeholder="Název složky"
                    className="text-xs"
                    style={{ ...inputStyle, flex: 1, minWidth: 0, padding: '4px 8px' }}
                    autoFocus
                    onKeyDown={e => { if (e.key === 'Enter') handleSave(); }}
                />
            </div>

            {/* Popis */}
            <input
                value={form.popis}
                onChange={e => setForm({ ...form, popis: e.target.value })}
                placeholder="Popis (volitelný)"
                className="text-xs mb-1"
                style={{ ...inputStyle, width: '100%', padding: '4px 8px' }}
            />

            {/* Akce */}
            <div className="flex gap-1">
                <button onClick={handleSave} disabled={saving} className="text-xs font-medium text-white" style={{ background: DS.accent, borderRadius: DS.radius, border: 'none', padding: '4px 10px', cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
                    {saving ? '…' : editId ? 'Uložit' : 'Vytvořit'}
                </button>
                <button onClick={onCancel} className="text-xs" style={{ color: DS.textSec, background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px' }}>Zrušit</button>
            </div>
        </div>
    );
}

// ── Rekurzivní položka stromu ──────────────────────────────
function TreeNode({ node, depth, activeSlozka, onSelect, isDark, isAdmin, onAction, onDropFile }) {
    const [expanded, setExpanded] = useState(depth < 1);
    const [dropHover, setDropHover] = useState(false);
    const hasChildren = node.children_tree && node.children_tree.length > 0;
    const isActive = activeSlozka === node.cesta;

    const handleDragOver = (e) => {
        // Přijmout interní drag (media-id) i pokud types nejsou viditelné — vždy povolit drop na složku
        e.preventDefault();
        e.stopPropagation();
        setDropHover(true);
    };
    const handleDragLeave = (e) => {
        e.stopPropagation();
        setDropHover(false);
    };
    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDropHover(false);
        const mediaId = e.dataTransfer.getData('application/media-id');
        if (mediaId) onDropFile?.(Number(mediaId), node.cesta, e);
    };

    return (
        <div>
            <div
                className="group flex items-center rounded-lg mb-0.5 transition-colors"
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                style={{
                    background: dropHover ? DS.accentBg : (isActive ? DS.accentBg15 : 'transparent'),
                    borderLeft: isActive ? `2px solid ${DS.accent}` : '2px solid transparent',
                    paddingLeft: depth * 12,
                    outline: dropHover ? `2px dashed ${DS.accent}` : 'none',
                    outlineOffset: -2,
                }}
            >
                {/* Expand/collapse */}
                <button
                    onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
                    className="p-0.5 shrink-0"
                    style={{ visibility: hasChildren ? 'visible' : 'hidden', width: 16 }}
                >
                    {expanded
                        ? <ChevronDown size={12} style={{ color: DS.textTer }} />
                        : <ChevronRight size={12} style={{ color: DS.textTer }} />
                    }
                </button>

                {/* Složka */}
                <button
                    onClick={() => onSelect(node.cesta)}
                    className="flex-1 text-left py-1.5 text-sm flex items-center justify-between min-w-0"
                    style={{ color: isActive ? DS.accentLight : DS.textMuted }}
                >
                    <span className="truncate">{node.ikona || '📁'} {node.nazev}</span>
                    <span className="text-xs ml-1 shrink-0" style={{ color: DS.textTer, fontFamily: 'JetBrains Mono, monospace', fontSize: 10 }}>
                        {node.celkem_souboru ?? node.pocet_souboru ?? 0}
                    </span>
                </button>

                {/* Admin akce */}
                {isAdmin && (
                    <div className="hidden group-hover:flex items-center gap-0.5 pr-1 shrink-0">
                        <button onClick={(e) => { e.stopPropagation(); onAction('add-child', node); }} className="p-0.5 rounded hover:opacity-70" title="Přidat podsložku">
                            <FolderPlus size={11} style={{ color: SECTION_COLOR }} />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); onAction('edit', node); }} className="p-0.5 rounded hover:opacity-70" title="Upravit">
                            <Pencil size={11} style={{ color: DS.textTer }} />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); onAction('delete', node); }} className="p-0.5 rounded hover:opacity-70" title="Smazat">
                            <Trash2 size={11} style={{ color: '#ef4444' }} />
                        </button>
                    </div>
                )}
            </div>

            {/* Děti */}
            {expanded && hasChildren && node.children_tree.map(child => (
                <TreeNode key={child.id} node={child} depth={depth + 1} activeSlozka={activeSlozka} onSelect={onSelect} isDark={isDark} isAdmin={isAdmin} onAction={onAction} onDropFile={onDropFile} />
            ))}
        </div>
    );
}

// ── Hlavní FolderTree ─────────────────────────────────────
function FolderTree({ strom, flatSlozky, celkemSouboru, activeSlozka, onSelect, cardStyle, isDark, isMobile, isAdmin, onSlozkyChange, onDropFile }) {
    const [formState, setFormState] = useState(null); // null | { editId, rodic_id, form }

    const handleAction = async (action, node) => {
        if (action === 'add-child') {
            setFormState({ editId: null, form: { nazev: '', ikona: '📁', popis: '', rodic_id: node.id } });
        } else if (action === 'edit') {
            setFormState({ editId: node.id, form: { nazev: node.nazev, ikona: node.ikona || '📁', popis: node.popis || '' } });
        } else if (action === 'delete') {
            if (!confirm(`Smazat složku "${node.nazev}"?`)) return;
            try {
                const r = await fetch(`/api/media/slozky/${node.id}`, { method: 'DELETE', headers: { 'X-CSRF-TOKEN': csrf() } });
                if (r.ok) { if (activeSlozka === node.cesta) onSelect(null); onSlozkyChange?.(); }
                else { const err = await r.json().catch(() => ({})); alert(err.error || 'Chyba'); }
            } catch (e) { console.error(e); }
        }
    };

    // Mobile — flat pills
    if (isMobile) {
        return (
            <div className="mb-3">
                <div className="flex gap-2 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none' }}>
                    <button
                        onClick={() => onSelect(null)}
                        className="px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap"
                        style={{
                            background: !activeSlozka ? SECTION_BG : (isDark ? 'rgba(255,255,255,0.06)' : 'var(--surface-raised)'),
                            color: !activeSlozka ? SECTION_COLOR : 'var(--text-secondary)',
                            border: `1px solid ${!activeSlozka ? SECTION_COLOR + '40' : 'var(--border-default)'}`,
                        }}
                    >
                        <Grid3X3 size={11} className="inline" /> Všechny ({celkemSouboru})
                    </button>
                    {(flatSlozky || []).map(sl => (
                        <button
                            key={sl.id}
                            onClick={() => onSelect(sl.cesta)}
                            className="px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap"
                            style={{
                                background: activeSlozka === sl.cesta ? SECTION_BG : (isDark ? 'rgba(255,255,255,0.06)' : 'var(--surface-raised)'),
                                color: activeSlozka === sl.cesta ? SECTION_COLOR : 'var(--text-secondary)',
                                border: `1px solid ${activeSlozka === sl.cesta ? SECTION_COLOR + '40' : 'var(--border-default)'}`,
                            }}
                        >
                            {sl.ikona || '📁'} {sl.nazev} ({sl.pocet_souboru})
                        </button>
                    ))}
                    {isAdmin && (
                        <button onClick={() => setFormState({ editId: null, form: { nazev: '', ikona: '📁', popis: '', rodic_id: null } })} className="px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap" style={{ background: SECTION_BG, color: SECTION_COLOR, border: `1px solid ${SECTION_COLOR}40` }}>
                            <Plus size={12} className="inline" /> Složka
                        </button>
                    )}
                </div>
                {formState && <SlozkaForm editId={formState.editId} initialForm={formState.form} isDark={isDark} onSave={() => { setFormState(null); onSlozkyChange?.(); }} onCancel={() => setFormState(null)} />}
            </div>
        );
    }

    // Desktop — strom
    return (
        <div style={{ background: DS.card, border: `1px solid ${DS.cardBorder}`, borderRadius: DS.radiusLg, width: 220, minWidth: 220, overflow: 'auto', maxHeight: 'calc(100vh - 220px)', padding: 12 }}>
            <div className="flex items-center justify-between mb-3">
                <span style={{ fontSize: 10, fontWeight: 500, color: DS.textTer, textTransform: 'uppercase', letterSpacing: '0.07em' }}>Složky</span>
                {isAdmin && (
                    <button onClick={() => setFormState({ editId: null, form: { nazev: '', ikona: '📁', popis: '', rodic_id: null } })} className="p-1 rounded hover:opacity-70" title="Nová složka">
                        <FolderPlus size={14} style={{ color: SECTION_COLOR }} />
                    </button>
                )}
            </div>

            {formState && <SlozkaForm editId={formState.editId} initialForm={formState.form} isDark={isDark} onSave={() => { setFormState(null); onSlozkyChange?.(); }} onCancel={() => setFormState(null)} />}

            <button
                onClick={() => onSelect(null)}
                className="w-full text-left px-2 py-1.5 rounded-lg text-sm flex items-center justify-between mb-1"
                style={{
                    background: !activeSlozka ? DS.accentBg15 : 'transparent',
                    borderLeft: !activeSlozka ? `2px solid ${DS.accent}` : '2px solid transparent',
                    color: !activeSlozka ? DS.accentLight : DS.textMuted,
                }}
            >
                <span className="flex items-center gap-1"><Grid3X3 size={12} /> Všechny soubory</span>
                <span className="text-xs" style={{ color: DS.textTer }}>{celkemSouboru}</span>
            </button>

            {(strom || []).map(node => (
                <TreeNode key={node.id} node={node} depth={0} activeSlozka={activeSlozka} onSelect={onSelect} isDark={isDark} isAdmin={isAdmin} onAction={handleAction} onDropFile={onDropFile} />
            ))}
        </div>
    );
}

// ═══════════════════════════════════════════════════════════
// ── DROP ACTION MENU ──────────────────────────────────────

function DropActionMenu({ x, y, onAction, isDark }) {
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onAction('cancel'); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [onAction]);

    const btnStyle = {
        display: 'flex', alignItems: 'center', gap: 6, width: '100%',
        padding: '6px 12px', border: 'none', background: 'transparent',
        color: DS.text, fontSize: 12, cursor: 'pointer', borderRadius: 5,
        textAlign: 'left',
    };
    const hoverBg = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)';

    return (
        <div
            ref={ref}
            className="fixed z-[1000] rounded-xl py-1 shadow-lg"
            style={{
                left: x, top: y, minWidth: 160,
                background: isDark ? 'rgba(30,30,30,0.95)' : 'rgba(255,255,255,0.98)',
                border: '1px solid ${DS.cardBorder}',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
            }}
        >
            <button style={btnStyle} onClick={() => onAction('move')} onMouseEnter={e => e.target.style.background = hoverBg} onMouseLeave={e => e.target.style.background = 'transparent'}>
                <ChevronRight size={14} style={{ color: SECTION_COLOR }} /> Přesunout sem
            </button>
            <button style={btnStyle} onClick={() => onAction('copy')} onMouseEnter={e => e.target.style.background = hoverBg} onMouseLeave={e => e.target.style.background = 'transparent'}>
                <Copy size={14} style={{ color: '#0CC0DF' }} /> Kopírovat sem
            </button>
            <div style={{ height: 1, margin: '4px 8px', background: DS.divider }} />
            <button style={btnStyle} onClick={() => onAction('cancel')} onMouseEnter={e => e.target.style.background = hoverBg} onMouseLeave={e => e.target.style.background = 'transparent'}>
                <X size={14} style={{ color: DS.textTer }} /> Zrušit
            </button>
        </div>
    );
}

// ── FILE GRID / LIST ──────────────────────────────────────

function FileGrid({ soubory, viewMode, selectedIds, onSelect, onOpen, cardStyle, isDark, onFileDragStart, onFileDragEnd }) {
    if (!soubory.length) {
        return (
            <div className="flex flex-col items-center justify-center py-16 text-center">
                <FolderOpen size={48} style={{ color: DS.textTer, opacity: 0.4 }} />
                <p className="mt-3 text-sm" style={{ color: DS.textTer }}>Žádné soubory</p>
            </div>
        );
    }

    if (viewMode === 'list') {
        return (
            <div className="rounded-xl overflow-hidden" style={cardStyle}>
                <table className="w-full text-sm">
                    <thead>
                        <tr style={{ borderBottom: `1px solid ${DS.cardBorder}` }}>
                            <th className="text-left px-3 py-2 font-medium" style={{ color: DS.textTer }}>Název</th>
                            <th className="text-left px-3 py-2 font-medium hidden md:table-cell" style={{ color: DS.textTer }}>Typ</th>
                            <th className="text-right px-3 py-2 font-medium hidden md:table-cell" style={{ color: DS.textTer }}>Velikost</th>
                            <th className="text-right px-3 py-2 font-medium hidden md:table-cell" style={{ color: DS.textTer }}>Datum</th>
                        </tr>
                    </thead>
                    <tbody>
                        {soubory.map(s => {
                            const cfg = getFileCfg(s);
                            const selected = selectedIds.includes(s.id);
                            return (
                                <tr
                                    key={s.id}
                                    draggable
                                    onDragStart={(e) => { e.dataTransfer.setData('application/media-id', String(s.id)); e.dataTransfer.effectAllowed = 'move'; onFileDragStart?.(s.id); }}
                                    onDragEnd={() => onFileDragEnd?.()}
                                    onClick={(e) => onSelect(s.id, e.ctrlKey || e.metaKey)}
                                    onDoubleClick={() => onOpen(s)}
                                    className="cursor-pointer transition-colors"
                                    style={{
                                        background: selected ? DS.accentBg15 : 'transparent',
                                        borderBottom: `1px solid ${DS.cardBorder}`,
                                    }}
                                >
                                    <td className="px-3 py-2">
                                        <div className="flex items-center gap-2">
                                            <cfg.Icon size={15} style={{ color: cfg.color, flexShrink: 0 }} />
                                            <span className="truncate" style={{ color: DS.textLight, maxWidth: 300 }}>{s.nazev}</span>
                                        </div>
                                    </td>
                                    <td className="px-3 py-2 hidden md:table-cell">
                                        <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: cfg.bg || (cfg.color + '20'), color: cfg.color }}>
                                            {cfg.label}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2 text-right hidden md:table-cell" style={{ color: DS.textSec, fontFamily: 'JetBrains Mono, monospace', fontSize: 12 }}>
                                        {formatSize(s.velikost)}
                                    </td>
                                    <td className="px-3 py-2 text-right hidden md:table-cell" style={{ color: DS.textTer, fontSize: 12 }}>
                                        {formatDate(s.created_at)}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    }

    // Grid view
    return (
        <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))' }}>
            {soubory.map(s => {
                const cfg = getFileCfg(s);
                const selected = selectedIds.includes(s.id);
                return (
                    <div
                        key={s.id}
                        draggable
                        onDragStart={(e) => { e.dataTransfer.setData('application/media-id', String(s.id)); e.dataTransfer.effectAllowed = 'move'; onFileDragStart?.(s.id); }}
                                    onDragEnd={() => onFileDragEnd?.()}
                        onClick={(e) => onSelect(s.id, e.ctrlKey || e.metaKey)}
                        onDoubleClick={() => onOpen(s)}
                        className="overflow-hidden cursor-pointer transition-all"
                        style={{
                            background: selected ? DS.accentBg15 : DS.card,
                            border: `1px solid ${selected ? DS.accent : DS.cardBorder}`,
                            borderRadius: DS.radiusCard,
                        }}
                    >
                        {/* Thumbnail / ikona */}
                        <div
                            className="flex items-center justify-center"
                            style={{
                                height: 86,
                                background: DS.inputBg,
                                overflow: 'hidden',
                            }}
                        >
                            {s.thumbnail_url ? (
                                <img
                                    src={s.thumbnail_url}
                                    alt={s.nazev}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                            ) : (
                                <cfg.Icon size={32} style={{ color: cfg.color, opacity: 0.7 }} />
                            )}
                        </div>
                        {/* Info */}
                        <div className="p-2">
                            <div className="truncate" style={{ color: DS.textLight, fontSize: 12 }}>
                                {s.nazev}
                            </div>
                            <div style={{ color: DS.textTer, fontSize: 11, fontFamily: 'JetBrains Mono, monospace', marginTop: 2 }}>
                                {formatSize(s.velikost)}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

// ═══════════════════════════════════════════════════════════
// ── DETAIL PANEL ──────────────────────────────────────────

function DetailPanel({ soubor, cardStyle, isDark, onClose, onDelete, onUpdate, onOpen }) {
    const [editNazev, setEditNazev] = useState(false);
    const [nazev, setNazev] = useState(soubor?.nazev || '');
    const [tagInput, setTagInput] = useState('');
    const [tagy, setTagy] = useState(soubor?.tagy || []);
    const [saving, setSaving] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (soubor) {
            setNazev(soubor.nazev || '');
            setTagy(soubor.tagy || []);
            setEditNazev(false);
        }
    }, [soubor?.id]);

    const cfg = soubor ? getFileCfg(soubor) : null;

    const handleSave = async (updates) => {
        if (!soubor) return;
        setSaving(true);
        try {
            const r = await fetch(`/api/media/${soubor.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrf() },
                body: JSON.stringify(updates),
            });
            if (r.ok) {
                const data = await r.json();
                onUpdate?.(data);
            }
        } catch (e) { console.error(e); }
        setSaving(false);
    };

    const handleAddTag = () => {
        const t = tagInput.trim().toLowerCase();
        if (t && !tagy.includes(t)) {
            const newTagy = [...tagy, t];
            setTagy(newTagy);
            handleSave({ tagy: newTagy });
        }
        setTagInput('');
    };

    const handleRemoveTag = (tag) => {
        const newTagy = tagy.filter(t => t !== tag);
        setTagy(newTagy);
        handleSave({ tagy: newTagy });
    };

    const handleCopyUrl = () => {
        if (!soubor) return;
        navigator.clipboard.writeText(soubor.url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const actionBtn = { display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: 5, border: `1px solid ${DS.cardBorder}`, background: 'transparent', cursor: 'pointer', transition: 'all 0.15s ease' };

    return (
        <div className="overflow-y-auto" style={{ background: DS.card, border: `1px solid ${DS.cardBorder}`, borderRadius: DS.radiusLg, padding: 16, width: 260, minWidth: 260, maxHeight: 'calc(100vh - 200px)' }}>
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
                <span style={{ fontSize: 10, fontWeight: 500, color: DS.textTer, textTransform: 'uppercase', letterSpacing: '0.07em' }}>Detail</span>
            </div>

            {!soubor ? (
                /* Prázdný stav */
                <div className="flex flex-col items-center justify-center py-10 text-center">
                    <Eye size={28} style={{ color: DS.textTer, opacity: 0.3 }} />
                    <p style={{ color: DS.textTer, fontSize: 12, marginTop: 8 }}>Vyberte soubor</p>
                    <p style={{ color: DS.textTer, fontSize: 11, opacity: 0.6, marginTop: 2 }}>Klikněte na soubor pro zobrazení detailu</p>
                </div>
            ) : (
                <>
                    {/* Akce — icon toolbar nad náhledem */}
                    <div className="flex items-center gap-1.5 mb-3">
                        <button onClick={() => onOpen(soubor)} style={actionBtn} title="Náhled"
                            onMouseEnter={e => { e.currentTarget.style.background = DS.accentBg15; e.currentTarget.style.borderColor = DS.accent; }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = DS.cardBorder; }}>
                            <Eye size={14} style={{ color: DS.accentLight }} />
                        </button>
                        <a href={soubor.url} download style={{ ...actionBtn, textDecoration: 'none' }} title="Stáhnout"
                            onMouseEnter={e => { e.currentTarget.style.background = DS.cardBorder; }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                            <Download size={14} style={{ color: DS.textSec }} />
                        </a>
                        <button onClick={handleCopyUrl} style={actionBtn} title={copied ? 'Zkopírováno!' : 'Kopírovat URL'}
                            onMouseEnter={e => { e.currentTarget.style.background = DS.cardBorder; }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                            {copied ? <Check size={14} style={{ color: DS.success }} /> : <Copy size={14} style={{ color: DS.textSec }} />}
                        </button>
                        <div style={{ flex: 1 }} />
                        <button onClick={() => { if (confirm('Opravdu smazat?')) onDelete(soubor.id); }} style={{ ...actionBtn, borderColor: 'transparent' }} title="Smazat"
                            onMouseEnter={e => { e.currentTarget.style.background = DS.dangerBg; e.currentTarget.style.borderColor = DS.danger + '40'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'transparent'; }}>
                            <Trash2 size={14} style={{ color: DS.danger }} />
                        </button>
                    </div>

                    {/* Preview */}
                    <div
                        className="flex items-center justify-center mb-3 overflow-hidden"
                        style={{ height: 150, borderRadius: 6, background: DS.inputBg }}
                    >
                        {soubor.thumbnail_url ? (
                            <img src={soubor.thumbnail_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                            <cfg.Icon size={44} style={{ color: cfg.color, opacity: 0.6 }} />
                        )}
                    </div>

                    {/* Název */}
                    {editNazev ? (
                        <div className="mb-2">
                            <input
                                value={nazev}
                                onChange={e => setNazev(e.target.value)}
                                onBlur={() => { handleSave({ nazev }); setEditNazev(false); }}
                                onKeyDown={e => { if (e.key === 'Enter') { handleSave({ nazev }); setEditNazev(false); } }}
                                className="w-full px-2 py-1 text-sm"
                                style={{ background: DS.inputBg, border: `1px solid ${DS.inputBorder}`, color: DS.text, borderRadius: DS.radius, colorScheme: 'dark', outline: `1px solid ${DS.accent}` }}
                                autoFocus
                            />
                        </div>
                    ) : (
                        <div
                            className="text-sm font-medium mb-1 cursor-pointer hover:opacity-80"
                            style={{ color: DS.text }}
                            onClick={() => setEditNazev(true)}
                            title="Klikni pro úpravu názvu"
                        >
                            {soubor.nazev}
                        </div>
                    )}

                    {/* Typ badge */}
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full mb-3" style={{ background: cfg.bg, color: cfg.color }}>
                        <cfg.Icon size={11} /> {cfg.label}
                    </span>

                    {/* Metadata */}
                    <div className="space-y-1.5 mb-4 text-xs" style={{ color: DS.textSec }}>
                        <div className="flex justify-between">
                            <span style={{ color: DS.textTer }}>Velikost</span>
                            <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>{formatSize(soubor.velikost)}</span>
                        </div>
                        {soubor.sirka && soubor.vyska && (
                            <div className="flex justify-between">
                                <span style={{ color: DS.textTer }}>Rozměry</span>
                                <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>{soubor.sirka} × {soubor.vyska} px</span>
                            </div>
                        )}
                        {soubor.delka_s && (
                            <div className="flex justify-between">
                                <span style={{ color: DS.textTer }}>Délka</span>
                                <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>{Math.floor(soubor.delka_s / 60)}:{String(soubor.delka_s % 60).padStart(2, '0')}</span>
                            </div>
                        )}
                        <div className="flex justify-between">
                            <span style={{ color: DS.textTer }}>MIME</span>
                            <span className="truncate ml-2">{soubor.mime_type}</span>
                        </div>
                        <div className="flex justify-between">
                            <span style={{ color: DS.textTer }}>Nahráno</span>
                            <span>{formatDate(soubor.created_at)}</span>
                        </div>
                        {soubor.nahral_uzivatel && (
                            <div className="flex justify-between">
                                <span style={{ color: DS.textTer }}>Nahrál</span>
                                <span>{soubor.nahral_uzivatel.jmeno} {soubor.nahral_uzivatel.prijmeni}</span>
                            </div>
                        )}
                    </div>

                    {/* Tagy */}
                    <div className="mb-4">
                        <div className="flex items-center gap-1 mb-2">
                            <Tag size={12} style={{ color: DS.textTer }} />
                            <span className="text-xs font-medium" style={{ color: DS.textTer }}>Tagy</span>
                        </div>
                        <div className="flex flex-wrap gap-1 mb-2">
                            {tagy.map(t => (
                                <span key={t} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full cursor-pointer"
                                    style={{ background: DS.accentBg, color: DS.accentLight, border: `1px solid ${DS.accent}30` }}
                                    onClick={() => handleRemoveTag(t)}
                                    title="Klikni pro odebrání"
                                >
                                    {t} <X size={10} />
                                </span>
                            ))}
                        </div>
                        <div className="flex gap-1">
                            <input
                                value={tagInput}
                                onChange={e => setTagInput(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') handleAddTag(); }}
                                placeholder="Přidat tag…"
                                className="flex-1 px-2 py-1 text-xs"
                                style={{ background: DS.inputBg, border: `1px solid ${DS.inputBorder}`, color: DS.text, borderRadius: DS.radius, colorScheme: 'dark' }}
                            />
                            <button onClick={handleAddTag} className="px-2 py-1 text-xs" style={{ background: DS.accentBg, color: DS.accentLight, borderRadius: DS.radius, border: 'none', cursor: 'pointer' }}>
                                <Plus size={12} />
                            </button>
                        </div>
                    </div>

                    {/* Použito v */}
                    {soubor.pouziti && soubor.pouziti.length > 0 && (
                        <div className="mb-4">
                            <div className="text-xs font-medium mb-2" style={{ color: DS.textTer }}>Použito v</div>
                            {soubor.pouziti.map(p => (
                                <div key={p.id} className="text-xs px-2 py-1 mb-1" style={{ background: DS.inputBg, color: DS.textSec, borderRadius: DS.radius }}>
                                    {p.modul} — {p.popis || p.entita_typ}
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

// ═══════════════════════════════════════════════════════════
// ── FILE VIEWER MODAL ─────────────────────────────────────

function FileViewerModal({ soubor, onClose, isDark }) {
    if (!soubor) return null;

    const isImage = soubor.typ === 'foto';
    const isPdf = soubor.mime_type === 'application/pdf';
    const isVideo = soubor.typ === 'video';
    const isAudio = soubor.typ === 'audio';
    const isOffice = /\.(docx?|xlsx?|pptx?)$/i.test(soubor.nazev) || soubor.mime_type?.includes('officedocument') || soubor.mime_type?.includes('msword') || soubor.mime_type?.includes('ms-excel') || soubor.mime_type?.includes('ms-powerpoint');

    return (
        <div
            className="fixed inset-0 z-[999] flex items-center justify-center"
            style={{ background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(8px)' }}
            onClick={onClose}
        >
            <div
                className="relative rounded-2xl overflow-hidden"
                style={{
                    width: '90vw', maxWidth: 1000,
                    height: '85vh',
                    background: isDark ? '#1a1a1a' : '#fff',
                }}
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: '1px solid ${DS.cardBorder}' }}>
                    <div className="flex items-center gap-2 truncate">
                        {(() => { const c = getFileCfg(soubor); return <c.Icon size={16} style={{ color: c.color, flexShrink: 0 }} />; })()}
                        <span className="text-sm font-medium truncate" style={{ color: DS.text }}>{soubor.nazev}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <a href={soubor.url} download className="p-2 rounded-lg hover:opacity-70">
                            <Download size={16} style={{ color: DS.textSec }} />
                        </a>
                        {isOffice && (
                            <a
                                href={`https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(soubor.url)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-2 rounded-lg hover:opacity-70"
                                title="Otevřít v Office Online"
                            >
                                <ExternalLink size={16} style={{ color: DS.textSec }} />
                            </a>
                        )}
                        <button onClick={onClose} className="p-2 rounded-lg hover:opacity-70">
                            <X size={16} style={{ color: DS.textSec }} />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="flex items-center justify-center" style={{ height: 'calc(100% - 52px)' }}>
                    {isImage && (
                        <img src={soubor.url} alt={soubor.nazev} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                    )}
                    {isPdf && (
                        <iframe src={soubor.url} style={{ width: '100%', height: '100%', border: 'none' }} />
                    )}
                    {isOffice && !isPdf && (
                        <iframe
                            src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(soubor.url)}`}
                            style={{ width: '100%', height: '100%', border: 'none' }}
                        />
                    )}
                    {isVideo && (
                        <video controls autoPlay style={{ maxWidth: '100%', maxHeight: '100%' }}>
                            <source src={soubor.url} type={soubor.mime_type} />
                        </video>
                    )}
                    {isAudio && (
                        <div className="flex flex-col items-center gap-6">
                            <Music size={64} style={{ color: DS.accentLight, opacity: 0.5 }} />
                            <audio controls autoPlay>
                                <source src={soubor.url} type={soubor.mime_type} />
                            </audio>
                        </div>
                    )}
                    {!isImage && !isPdf && !isVideo && !isAudio && !isOffice && (
                        <div className="text-center p-8">
                            {(() => { const c = getFileCfg(soubor); return <c.Icon size={56} style={{ color: c.color, opacity: 0.5 }} />; })()}
                            <p className="mt-4 text-sm" style={{ color: DS.textSec }}>Náhled není dostupný</p>
                            <a href={soubor.url} download className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium" style={{ background: SECTION_BG, color: SECTION_COLOR }}>
                                <Download size={16} /> Stáhnout soubor
                            </a>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════
// ── PŘÍSTUPY TAB ──────────────────────────────────────────

function PristupyTab({ cardStyle, isDark, user }) {
    const [slozky, setSlozky] = useState([]);
    const [activeSlozka, setActiveSlozka] = useState(null);
    const [pristupy, setPristupy] = useState([]);
    const [efektivni, setEfektivni] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showAdd, setShowAdd] = useState(false);
    const [addForm, setAddForm] = useState({ skupina_typ: 'role', skupina_id: '', uroven: 'cist', poznamka: '' });
    const [ciselniky, setCiselniky] = useState({ role: [], pozice: [], oddeleni: [], kariera: [], uzivatele: [] });

    useEffect(() => {
        fetch('/api/media/slozky', { headers: { 'X-CSRF-TOKEN': csrf() } })
            .then(r => r.json())
            .then(data => setSlozky(data.slozky || []))
            .catch(() => {});

        // Načíst číselníky
        Promise.all([
            fetch('/api/ciselniky/pozice').then(r => r.json()).catch(() => []),
            fetch('/api/ciselniky/oddeleni').then(r => r.json()).catch(() => []),
            fetch('/api/ciselniky/kariery').then(r => r.json()).catch(() => []),
            fetch('/api/uzivatele').then(r => r.json()).catch(() => []),
        ]).then(([pozice, oddeleni, kariery, uzivatele]) => {
            setCiselniky({
                role: [{ id: 1, nazev: 'Superadmin' }, { id: 2, nazev: 'Admin' }, { id: 3, nazev: 'Uživatel' }],
                pozice: pozice || [],
                oddeleni: oddeleni || [],
                kariera: kariery || [],
                uzivatele: (uzivatele || []).map(u => ({ id: u.id, nazev: `${u.jmeno} ${u.prijmeni}` })),
            });
        });
    }, []);

    useEffect(() => {
        if (!activeSlozka) { setPristupy([]); setEfektivni([]); return; }
        setLoading(true);
        Promise.all([
            fetch(`/api/media/pristupy?slozka=${encodeURIComponent(activeSlozka)}`, { headers: { 'X-CSRF-TOKEN': csrf() } }).then(r => r.json()),
            fetch(`/api/media/efektivni-pristup?slozka=${encodeURIComponent(activeSlozka)}`, { headers: { 'X-CSRF-TOKEN': csrf() } }).then(r => r.json()),
        ]).then(([p, e]) => {
            setPristupy(p || []);
            setEfektivni(e || []);
        }).catch(() => {}).finally(() => setLoading(false));
    }, [activeSlozka]);

    const handleAdd = async () => {
        if (!addForm.skupina_id) return;
        try {
            const r = await fetch('/api/media/pristupy', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrf() },
                body: JSON.stringify({ ...addForm, slozka: activeSlozka, skupina_id: Number(addForm.skupina_id) }),
            });
            if (r.ok) {
                const novy = await r.json();
                setPristupy([...pristupy, novy]);
                setShowAdd(false);
                setAddForm({ skupina_typ: 'role', skupina_id: '', uroven: 'cist', poznamka: '' });
                // Refresh efektivní
                fetch(`/api/media/efektivni-pristup?slozka=${encodeURIComponent(activeSlozka)}`).then(r => r.json()).then(e => setEfektivni(e || []));
            }
        } catch (e) { console.error(e); }
    };

    const handleDelete = async (id) => {
        if (!confirm('Smazat tento přístup?')) return;
        try {
            const r = await fetch(`/api/media/pristupy/${id}`, { method: 'DELETE', headers: { 'X-CSRF-TOKEN': csrf() } });
            if (r.ok) {
                setPristupy(pristupy.filter(p => p.id !== id));
                fetch(`/api/media/efektivni-pristup?slozka=${encodeURIComponent(activeSlozka)}`).then(r => r.json()).then(e => setEfektivni(e || []));
            }
        } catch (e) { console.error(e); }
    };

    const skupinyOptions = ciselniky[addForm.skupina_typ === 'uzivatel' ? 'uzivatele' : addForm.skupina_typ] || [];

    return (
        <div className="flex gap-4" style={{ minHeight: 400 }}>
            {/* Folder list */}
            <div className="rounded-xl p-3 shrink-0" style={{ ...cardStyle, width: 260 }}>
                <div className="text-xs font-semibold mb-3 uppercase tracking-wider" style={{ color: DS.textTer }}>Složky</div>
                {slozky.map(sl => (
                    <button
                        key={sl.id}
                        onClick={() => setActiveSlozka(sl.cesta)}
                        className="w-full text-left px-2 py-2 rounded-lg text-sm flex items-center justify-between mb-1"
                        style={{
                            background: activeSlozka === sl.cesta ? SECTION_BG : 'transparent',
                            color: activeSlozka === sl.cesta ? SECTION_COLOR : 'var(--text-secondary)',
                        }}
                    >
                        <span>{sl.ikona || '📁'} {sl.nazev}</span>
                        <ChevronRight size={14} style={{ opacity: 0.4 }} />
                    </button>
                ))}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
                {!activeSlozka ? (
                    <div className="flex flex-col items-center justify-center py-16">
                        <Shield size={48} style={{ color: DS.textTer, opacity: 0.4 }} />
                        <p className="mt-3 text-sm" style={{ color: DS.textTer }}>Vyberte složku pro správu přístupů</p>
                    </div>
                ) : loading ? (
                    <div className="flex items-center justify-center py-16">
                        <Loader2 size={24} className="animate-spin" style={{ color: SECTION_COLOR }} />
                    </div>
                ) : (
                    <div className="space-y-4">
                        {/* Přístupy */}
                        <div className="rounded-xl p-4" style={cardStyle}>
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-sm font-semibold" style={{ color: DS.text }}>Přístupy</h3>
                                <button
                                    onClick={() => setShowAdd(!showAdd)}
                                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium"
                                    style={{ background: SECTION_BG, color: SECTION_COLOR }}
                                >
                                    <Plus size={14} /> Přidat
                                </button>
                            </div>

                            {/* Add form */}
                            {showAdd && (
                                <div className="rounded-lg p-3 mb-4" style={{ background: isDark ? DS.inputBg : 'rgba(0,0,0,0.02)', border: '1px solid ${DS.cardBorder}' }}>
                                    <div className="grid grid-cols-2 gap-2 mb-2">
                                        <select
                                            value={addForm.skupina_typ}
                                            onChange={e => setAddForm({ ...addForm, skupina_typ: e.target.value, skupina_id: '' })}
                                            className="rounded-lg px-2 py-1.5 text-xs"
                                            style={{ background: DS.inputBg, border: `1px solid ${DS.inputBorder}`, color: DS.text, borderRadius: DS.radius, colorScheme: 'dark' }}
                                        >
                                            {Object.entries(SKUPINA_TYP_LABELS).map(([k, v]) => (
                                                <option key={k} value={k}>{v}</option>
                                            ))}
                                        </select>
                                        <select
                                            value={addForm.skupina_id}
                                            onChange={e => setAddForm({ ...addForm, skupina_id: e.target.value })}
                                            className="rounded-lg px-2 py-1.5 text-xs"
                                            style={{ background: DS.inputBg, border: `1px solid ${DS.inputBorder}`, color: DS.text, borderRadius: DS.radius, colorScheme: 'dark' }}
                                        >
                                            <option value="">Vyberte…</option>
                                            {skupinyOptions.map(o => (
                                                <option key={o.id} value={o.id}>{o.nazev}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="flex gap-2 items-center">
                                        <select
                                            value={addForm.uroven}
                                            onChange={e => setAddForm({ ...addForm, uroven: e.target.value })}
                                            className="rounded-lg px-2 py-1.5 text-xs"
                                            style={{ background: DS.inputBg, border: `1px solid ${DS.inputBorder}`, color: DS.text, borderRadius: DS.radius, colorScheme: 'dark' }}
                                        >
                                            {Object.entries(UROVEN_CFG).map(([k, v]) => (
                                                <option key={k} value={k}>{v.label}</option>
                                            ))}
                                        </select>
                                        <button onClick={handleAdd} className="px-3 py-1.5 rounded-lg text-xs font-medium text-white" style={{ background: SECTION_COLOR }}>
                                            Uložit
                                        </button>
                                        <button onClick={() => setShowAdd(false)} className="px-3 py-1.5 rounded-lg text-xs" style={{ color: DS.textSec }}>
                                            Zrušit
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Seznam přístupů */}
                            {pristupy.length === 0 ? (
                                <p className="text-xs" style={{ color: DS.textTer }}>Žádné přístupy definovány</p>
                            ) : (
                                <div className="space-y-1.5">
                                    {pristupy.map(p => {
                                        const uCfg = UROVEN_CFG[p.uroven] || UROVEN_CFG.cist;
                                        return (
                                            <div key={p.id} className="flex items-center justify-between px-3 py-2 rounded-lg" style={{ background: isDark ? DS.inputBg : 'rgba(0,0,0,0.02)' }}>
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)', color: DS.textTer }}>
                                                        {SKUPINA_TYP_LABELS[p.skupina_typ] || p.skupina_typ}
                                                    </span>
                                                    <span className="text-sm truncate" style={{ color: DS.text }}>
                                                        {p.skupina_nazev || `#${p.skupina_id}`}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: uCfg.bg, color: uCfg.color }}>
                                                        {uCfg.label}
                                                    </span>
                                                    <button onClick={() => handleDelete(p.id)} className="p-1 rounded hover:opacity-70">
                                                        <Trash2 size={12} style={{ color: '#ef4444' }} />
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Efektivní přehled */}
                        <div className="rounded-xl p-4" style={cardStyle}>
                            <h3 className="text-sm font-semibold mb-3" style={{ color: DS.text }}>Efektivní přístupy</h3>
                            {efektivni.length === 0 ? (
                                <p className="text-xs" style={{ color: DS.textTer }}>Žádní uživatelé s přístupem</p>
                            ) : (
                                <div className="space-y-2">
                                    {['plny', 'nahravat', 'cist'].map(ur => {
                                        const uzivatele = efektivni.filter(e => e.uroven === ur);
                                        if (!uzivatele.length) return null;
                                        const uCfg = UROVEN_CFG[ur];
                                        return (
                                            <div key={ur} className="flex items-start gap-3 px-3 py-2 rounded-lg" style={{ background: uCfg.bg }}>
                                                <span className="text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap" style={{ background: uCfg.color + '30', color: uCfg.color }}>
                                                    {uCfg.label}
                                                </span>
                                                <div className="flex flex-wrap gap-1">
                                                    {uzivatele.map(u => (
                                                        <span key={u.uzivatel_id} className="text-xs" style={{ color: DS.text }}>
                                                            {u.jmeno} {u.prijmeni}
                                                            {u !== uzivatele[uzivatele.length - 1] ? ',' : ''}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════
// ── MAIN EXPORT ───────────────────────────────────────────

export default function Media({ user, onUpload }) {
    const { theme } = useTheme();
    const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const isAdmin = user?.role === 'superadmin' || user?.role === 'admin';
    const tabs = ALL_TABS.filter(t => !t.admin || isAdmin);

    const [activeTab, setActiveTab] = useState('spravce');
    const swipeProps = useSwipeTabs(tabs.map(t => t.id), activeTab, setActiveTab);

    // Správce souborů state
    const [strom, setStrom] = useState([]);
    const [flatSlozky, setFlatSlozky] = useState([]);
    const [celkemSouboru, setCelkemSouboru] = useState(0);
    const [soubory, setSoubory] = useState([]);
    const [stranka, setStranka] = useState(1);
    const [posledniStranka, setPosledniStranka] = useState(1);
    const [loadingMore, setLoadingMore] = useState(false);
    const [activeSlozka, setActiveSlozka] = useState(null);
    const [viewMode, setViewMode] = useState('grid');
    const [hledat, setHledat] = useState('');
    const [filtrTyp, setFiltrTyp] = useState('');
    const [razeni, setRazeni] = useState('created_at');
    const [smer, setSmer] = useState('desc');
    const [selectedIds, setSelectedIds] = useState([]);
    const [detailSoubor, setDetailSoubor] = useState(null);
    const [viewerSoubor, setViewerSoubor] = useState(null);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const [dropMenu, setDropMenu] = useState(null);
    const [bulkAction, setBulkAction] = useState(null); // null | 'presunout' | 'kopirovat'
    const internalDragRef = useRef(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        const handler = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handler);
        return () => window.removeEventListener('resize', handler);
    }, []);

    const cardStyle = isDark ? {
        background: DS.card,
        border: `1px solid ${DS.cardBorder}`,
        borderRadius: DS.radiusCard,
    } : {
        background: 'var(--surface-raised)',
        border: '1px solid ${DS.cardBorder}',
        boxShadow: 'var(--shadow-xs)',
    };

    const selectStyle = {
        background: isDark ? DS.inputBg : 'var(--surface-raised)',
        border: `1px solid ${isDark ? DS.inputBorder : 'var(--border-default)'}`,
        color: isDark ? DS.text : 'var(--text-primary)',
        colorScheme: isDark ? 'dark' : 'light',
        borderRadius: DS.radius,
        cursor: 'pointer',
    };

    // Načtení složek (strom)
    const fetchSlozky = useCallback(() => {
        fetch('/api/media/slozky')
            .then(r => r.json())
            .then(data => {
                setStrom(data.strom || []);
                setFlatSlozky(data.slozky || []);
                setCelkemSouboru(data.celkem_souboru || 0);
            })
            .catch(() => {});
    }, []);

    useEffect(() => { fetchSlozky(); }, [fetchSlozky]);

    // Načtení souborů (page=1 → nahradit, page>1 → připojit)
    const fetchSoubory = useCallback((page = 1) => {
        if (page === 1) setLoading(true);
        else setLoadingMore(true);

        const params = new URLSearchParams();
        if (activeSlozka) params.set('slozka', activeSlozka);
        if (filtrTyp) params.set('typ', filtrTyp);
        if (hledat) params.set('hledat', hledat);
        params.set('razeni', razeni);
        params.set('smer', smer);
        params.set('page', String(page));

        fetch(`/api/media?${params}`)
            .then(r => r.json())
            .then(data => {
                if (page === 1) {
                    setSoubory(data.soubory || []);
                } else {
                    setSoubory(prev => [...prev, ...(data.soubory || [])]);
                }
                setStranka(data.stranka || 1);
                setPosledniStranka(data.posledni_stranka || 1);
            })
            .catch(() => {})
            .finally(() => {
                setLoading(false);
                setLoadingMore(false);
            });
    }, [activeSlozka, filtrTyp, hledat, razeni, smer]);

    useEffect(() => { setStranka(1); fetchSoubory(1); }, [fetchSoubory]);

    // Načtení další stránky (infinite scroll)
    const loadNextPage = useCallback(() => {
        if (loadingMore || loading || stranka >= posledniStranka) return;
        fetchSoubory(stranka + 1);
    }, [loadingMore, loading, stranka, posledniStranka, fetchSoubory]);

    // IntersectionObserver pro infinite scroll
    const sentinelRef = useRef(null);
    useEffect(() => {
        const el = sentinelRef.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) loadNextPage(); },
            { rootMargin: '200px' }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, [loadNextPage]);

    // Refresh obojí
    const refreshAll = useCallback(() => { fetchSlozky(); fetchSoubory(); }, [fetchSlozky, fetchSoubory]);

    // Upload — deleguje na app-level handler (popup žije v app.jsx)
    const handleUpload = async (files) => {
        if (!files || !files.length) return;
        const fileArr = Array.from(files);          // kopie PŘED resetem inputu
        if (fileInputRef.current) fileInputRef.current.value = '';
        setUploading(true);
        await onUpload(fileArr, activeSlozka);
        refreshAll();
        setUploading(false);
    };

    // Drag & drop upload z OS (ignoruje interní drag souborů mezi složkami)
    const handleDragOver = (e) => {
        if (internalDragRef.current) return; // interní drag — nechat složky zachytit
        if (e.dataTransfer.types.includes('Files')) {
            e.preventDefault();
            setDragOver(true);
        }
    };
    const handleDragLeave = () => setDragOver(false);
    const handleDrop = (e) => {
        if (internalDragRef.current) { internalDragRef.current = false; return; }
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            e.preventDefault();
            setDragOver(false);
            handleUpload(e.dataTransfer.files);
        }
    };

    // Drag & drop — zobrazí kontextové menu s volbami
    const handleDropFile = (souborId, cilovaSlozka, event) => {
        // Pozice menu — u kurzoru myši
        const x = event?.clientX ?? window.innerWidth / 2;
        const y = event?.clientY ?? window.innerHeight / 2;
        setDropMenu({ souborId, slozka: cilovaSlozka, x, y });
    };

    const handleDropAction = async (action) => {
        if (!dropMenu || action === 'cancel') { setDropMenu(null); return; }
        const { souborId, slozka } = dropMenu;
        setDropMenu(null);

        try {
            if (action === 'move') {
                const r = await fetch(`/api/media/${souborId}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrf() },
                    body: JSON.stringify({ slozka }),
                });
                if (r.ok) refreshAll();
            } else if (action === 'copy') {
                const r = await fetch(`/api/media/${souborId}/copy`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrf() },
                    body: JSON.stringify({ slozka }),
                });
                if (r.ok) refreshAll();
            }
        } catch (e) { console.error(e); }
    };

    // Selekce
    const handleSelect = (id, multi) => {
        if (multi) {
            setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
        } else {
            setSelectedIds([id]);
            // Načíst detail
            fetch(`/api/media/${id}`)
                .then(r => r.json())
                .then(data => setDetailSoubor(data))
                .catch(() => {});
        }
    };

    // Smazání
    const handleDelete = async (id) => {
        try {
            const r = await fetch(`/api/media/${id}`, { method: 'DELETE', headers: { 'X-CSRF-TOKEN': csrf() } });
            if (r.ok) {
                if (detailSoubor?.id === id) setDetailSoubor(null);
                setSelectedIds(prev => prev.filter(i => i !== id));
                refreshAll();
            }
        } catch (e) { console.error(e); }
    };

    // Hromadné operace
    const handleBulkAction = async (akce, cilovaSlozka) => {
        if (!selectedIds.length) return;
        if (akce === 'smazat' && !confirm(`Smazat ${selectedIds.length} souborů?`)) return;
        try {
            const r = await fetch('/api/media/hromadne', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrf() },
                body: JSON.stringify({ akce, ids: selectedIds, slozka: cilovaSlozka }),
            });
            if (r.ok) {
                setSelectedIds([]);
                setDetailSoubor(null);
                refreshAll();
            }
        } catch (e) { console.error(e); }
    };

    const handleSelectAll = () => {
        if (selectedIds.length === soubory.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(soubory.map(s => s.id));
        }
    };

    // Update z detail panelu
    const handleUpdate = (updated) => {
        setDetailSoubor(updated);
        fetchSoubory();
    };

    return (
        <div className="p-4 md:p-6">
            {/* Header — na mobilu se zobrazuje v TopBaru, tady skryjeme */}
            {!isMobile && (
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                    <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center" style={{ width: 44, height: 44, borderRadius: DS.radiusLg, background: DS.accentBg }}>
                            <FolderOpen size={22} style={{ color: DS.accentLight }} />
                        </div>
                        <div>
                            <h1 style={{ fontSize: 20, fontWeight: 500, color: '#fff', margin: 0 }}>Média & Soubory</h1>
                            <p style={{ fontSize: 13, color: DS.textSec, margin: 0 }}>Centrální správce souborů</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Záložky kreslí TabBar. Správce souborů není modul v katalogu,
                nabídku si proto nese sám (`navic`); na mobilu ji přepíná střed
                lišty. Aktivní záložka měla barvu textu natvrdo bílou — ve
                světlém motivu zmizela. */}
            <div style={{ marginBottom: isMobile ? 0 : 24 }}>
                <TabBar user={null} navic={tabs.map(t => ({ id: t.id, nazev: t.label, Icon: t.Icon }))}
                    aktivni={activeTab} onZmena={setActiveTab}
                    barva={DS.accentLight} isDark={isDark} isMobile={isMobile} />
            </div>

            {/* Tab content */}
            <div {...swipeProps}>
                {activeTab === 'spravce' && (
                    <div>
                        {/* Toolbar */}
                        <div className="flex flex-wrap items-center gap-2 mb-4">
                            <button
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploading}
                                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white"
                                style={{ background: DS.accent, borderRadius: DS.radius, opacity: uploading ? 0.6 : 1, border: 'none', cursor: 'pointer' }}
                            >
                                {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                                {uploading ? 'Nahrávám…' : 'Nahrát'}
                            </button>
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                className="hidden"
                                onChange={e => handleUpload(e.target.files)}
                            />

                            <div className="flex-1" />

                            {/* Search */}
                            <div className="relative">
                                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: DS.textTer }} />
                                <input
                                    value={hledat}
                                    onChange={e => setHledat(e.target.value)}
                                    placeholder="Hledat…"
                                    className="pl-8 pr-3 py-2 rounded-lg text-sm w-40 md:w-56"
                                    style={{ ...selectStyle, cursor: 'text' }}
                                />
                            </div>

                            {/* Filtr typ */}
                            <select
                                value={filtrTyp}
                                onChange={e => setFiltrTyp(e.target.value)}
                                className="rounded-lg px-2 py-2 text-sm"
                                style={selectStyle}
                            >
                                <option value="">Všechny typy</option>
                                {Object.entries(TYP_CFG).map(([k, v]) => (
                                    <option key={k} value={k}>{v.label}</option>
                                ))}
                            </select>

                            {/* Řazení */}
                            <select
                                value={`${razeni}_${smer}`}
                                onChange={e => {
                                    const [r, s] = e.target.value.split('_');
                                    setRazeni(r);
                                    setSmer(s);
                                }}
                                className="rounded-lg px-2 py-2 text-sm hidden md:block"
                                style={selectStyle}
                            >
                                <option value="created_at_desc">Nejnovější</option>
                                <option value="created_at_asc">Nejstarší</option>
                                <option value="nazev_asc">A → Z</option>
                                <option value="nazev_desc">Z → A</option>
                                <option value="velikost_desc">Největší</option>
                                <option value="velikost_asc">Nejmenší</option>
                            </select>

                            {/* View mode */}
                            {!isMobile && (
                                <div className="flex overflow-hidden" style={{ border: `1px solid ${DS.tabBorder}`, borderRadius: DS.radius }}>
                                    <button
                                        onClick={() => setViewMode('grid')}
                                        className="p-2"
                                        style={{ background: viewMode === 'grid' ? DS.cardBorder : 'transparent', border: 'none', cursor: 'pointer' }}
                                    >
                                        <Grid3X3 size={16} style={{ color: viewMode === 'grid' ? DS.text : DS.textTer }} />
                                    </button>
                                    <button
                                        onClick={() => setViewMode('list')}
                                        className="p-2"
                                        style={{ background: viewMode === 'list' ? DS.cardBorder : 'transparent', border: 'none', cursor: 'pointer' }}
                                    >
                                        <List size={16} style={{ color: viewMode === 'list' ? DS.text : DS.textTer }} />
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Bulk action bar */}
                        {selectedIds.length > 1 && (
                            <div className="flex items-center gap-2 mb-3 px-3 py-2" style={{
                                background: DS.card,
                                border: `1px solid ${DS.cardBorder}`,
                                borderRadius: DS.radiusCard,
                                borderTop: `1px solid ${DS.cardBorder}`,
                            }}>
                                <button onClick={handleSelectAll} className="flex items-center gap-1.5 text-xs font-medium" style={{ color: SECTION_COLOR, background: 'none', border: 'none', cursor: 'pointer' }}>
                                    {selectedIds.length === soubory.length
                                        ? <CheckSquare size={14} />
                                        : <Square size={14} />
                                    }
                                    {selectedIds.length === soubory.length ? 'Zrušit výběr' : 'Vybrat vše'}
                                </button>
                                <span className="text-xs font-medium" style={{ color: SECTION_COLOR }}>
                                    — {selectedIds.length} vybráno
                                </span>

                                <div className="flex-1" />

                                {/* Přesunout */}
                                {!bulkAction && (
                                    <button onClick={() => setBulkAction('presunout')} className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium" style={{ background: 'transparent', color: DS.text, border: `1px solid ${DS.tabBorder}`, borderRadius: DS.radius, cursor: 'pointer' }}>
                                        <Move size={12} /> Přesunout
                                    </button>
                                )}
                                {/* Kopírovat */}
                                {!bulkAction && (
                                    <button onClick={() => setBulkAction('kopirovat')} className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium" style={{ background: 'transparent', color: DS.text, border: `1px solid ${DS.tabBorder}`, borderRadius: DS.radius, cursor: 'pointer' }}>
                                        <Copy size={12} /> Kopírovat
                                    </button>
                                )}
                                {/* Smazat */}
                                {!bulkAction && (
                                    <button onClick={() => handleBulkAction('smazat')} className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium" style={{ background: DS.dangerBg, color: DS.danger, border: 'none', cursor: 'pointer' }}>
                                        <Trash2 size={12} /> Smazat
                                    </button>
                                )}

                                {/* Výběr cílové složky */}
                                {bulkAction && (
                                    <>
                                        <span className="text-xs" style={{ color: DS.textSec }}>
                                            {bulkAction === 'presunout' ? 'Přesunout do:' : 'Kopírovat do:'}
                                        </span>
                                        <select
                                            onChange={e => { if (e.target.value) { handleBulkAction(bulkAction, e.target.value); setBulkAction(null); } }}
                                            className="rounded-lg px-2 py-1 text-xs"
                                            style={selectStyle}
                                            defaultValue=""
                                        >
                                            <option value="" disabled>Vyberte složku…</option>
                                            {flatSlozky.map(sl => (
                                                <option key={sl.id} value={sl.cesta}>{sl.nazev}</option>
                                            ))}
                                        </select>
                                        <button onClick={() => setBulkAction(null)} className="p-1 rounded hover:opacity-70" style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                                            <X size={14} style={{ color: DS.textTer }} />
                                        </button>
                                    </>
                                )}

                                {/* Zrušit výběr */}
                                {!bulkAction && (
                                    <button onClick={() => setSelectedIds([])} className="p-1 rounded hover:opacity-70" style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                                        <X size={14} style={{ color: DS.textTer }} />
                                    </button>
                                )}
                            </div>
                        )}

                        {/* 3-column layout */}
                        <div
                            className="flex gap-4"
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            style={{ position: 'relative' }}
                        >
                            {/* Drag overlay */}
                            {dragOver && (
                                <div
                                    className="absolute inset-0 z-50 flex items-center justify-center"
                                    style={{
                                        background: 'rgba(108,65,146,0.08)',
                                        border: `1px dashed ${DS.tabBorder}`,
                                        borderRadius: 6,
                                    }}
                                >
                                    <div className="text-center">
                                        <Upload size={40} style={{ color: DS.textSec }} />
                                        <p className="mt-2 text-sm font-medium" style={{ color: DS.textSec }}>Přetáhněte soubory sem</p>
                                    </div>
                                </div>
                            )}

                            {/* Folder tree */}
                            {!isMobile && (
                                <FolderTree
                                    strom={strom}
                                    flatSlozky={flatSlozky}
                                    celkemSouboru={celkemSouboru}
                                    activeSlozka={activeSlozka}
                                    onSelect={s => { setActiveSlozka(s); setSelectedIds([]); setDetailSoubor(null); }}
                                    cardStyle={cardStyle}
                                    isDark={isDark}
                                    isAdmin={isAdmin}
                                    onSlozkyChange={refreshAll}
                                    onDropFile={handleDropFile}
                                />
                            )}

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                                {isMobile && (
                                    <FolderTree
                                        strom={strom}
                                        flatSlozky={flatSlozky}
                                        celkemSouboru={celkemSouboru}
                                        activeSlozka={activeSlozka}
                                        onSelect={s => { setActiveSlozka(s); setSelectedIds([]); setDetailSoubor(null); }}
                                        cardStyle={cardStyle}
                                        isDark={isDark}
                                        isMobile
                                        isAdmin={isAdmin}
                                        onSlozkyChange={refreshAll}
                                        onDropFile={handleDropFile}
                                    />
                                )}

                                {loading ? (
                                    <div className="flex items-center justify-center py-16">
                                        <Loader2 size={24} className="animate-spin" style={{ color: SECTION_COLOR }} />
                                    </div>
                                ) : (
                                    <>
                                        <FileGrid
                                            soubory={soubory}
                                            viewMode={isMobile ? 'grid' : viewMode}
                                            selectedIds={selectedIds}
                                            onSelect={handleSelect}
                                            onOpen={s => setViewerSoubor(s)}
                                            cardStyle={cardStyle}
                                            isDark={isDark}
                                            onFileDragStart={() => { internalDragRef.current = true; }}
                                            onFileDragEnd={() => { internalDragRef.current = false; }}
                                        />

                                        {/* Infinite scroll sentinel */}
                                        <div ref={sentinelRef} style={{ height: 1 }} />

                                        {loadingMore && (
                                            <div className="flex items-center justify-center py-6">
                                                <Loader2 size={20} className="animate-spin" style={{ color: SECTION_COLOR }} />
                                                <span className="ml-2 text-xs" style={{ color: DS.textSec }}>Načítám další…</span>
                                            </div>
                                        )}

                                        {!loadingMore && soubory.length > 0 && stranka >= posledniStranka && (
                                            <div className="text-center py-4">
                                                <span className="text-xs" style={{ color: DS.textTer }}>
                                                    Zobrazeno všech {soubory.length} souborů
                                                </span>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>

                            {/* Detail panel — vždy viditelný */}
                            {!isMobile && (
                                <DetailPanel
                                    soubor={detailSoubor}
                                    cardStyle={cardStyle}
                                    isDark={isDark}
                                    onClose={() => { setDetailSoubor(null); setSelectedIds([]); }}
                                    onDelete={handleDelete}
                                    onUpdate={handleUpdate}
                                    onOpen={s => setViewerSoubor(s)}
                                />
                            )}
                        </div>

                        {/* Mobile detail overlay */}
                        {isMobile && detailSoubor && (
                            <div className="fixed inset-0 z-[998]" style={{ background: 'rgba(0,0,0,0.6)' }} onClick={() => setDetailSoubor(null)}>
                                <div
                                    className="absolute right-0 top-0 bottom-0 overflow-y-auto"
                                    style={{ width: '85vw', maxWidth: 320, background: isDark ? '#1a1a1a' : '#fff' }}
                                    onClick={e => e.stopPropagation()}
                                >
                                    <div className="p-4">
                                        <DetailPanel
                                            soubor={detailSoubor}
                                            cardStyle={cardStyle}
                                            isDark={isDark}
                                            onClose={() => { setDetailSoubor(null); setSelectedIds([]); }}
                                            onDelete={handleDelete}
                                            onUpdate={handleUpdate}
                                            onOpen={s => setViewerSoubor(s)}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'pristupy' && (
                    <PristupyTab cardStyle={cardStyle} isDark={isDark} user={user} />
                )}
            </div>

            {/* File viewer modal */}
            {viewerSoubor && (
                <FileViewerModal soubor={viewerSoubor} onClose={() => setViewerSoubor(null)} isDark={isDark} />
            )}

            {dropMenu && (
                <DropActionMenu x={dropMenu.x} y={dropMenu.y} onAction={handleDropAction} isDark={isDark} />
            )}

        </div>
    );
}
